import { useQuery } from '@apollo/client';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useMemo } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ONBOARDING_TOOLTIP_CLASS_NAME from '../../theme/constants/tooltip';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import useBooleanState from '../../hooks/useBooleanState';
import CESSIONS_SUMMARY from '../../graphql/queries/cessions-summary';
import SurplusDialog from './SurplusDialog';
import { joinWithAnd } from '../../helpers/arrays';

const Surplus = () => {
  const [open, , setTrue, setFalse] = useBooleanState(false);
  const selectedCompany = useSelectedCompany();

  const { data: cessionsSummary } = useQuery(CESSIONS_SUMMARY, {
    variables: { companyId: selectedCompany?.id },
  });

  const amortizationDebt = selectedCompany?.masterEntity.debtAmortizationSchedule?.count > 0;

  const cantWithdrawSurplus = useMemo(
    () => cessionsSummary?.cessionsSummary.debtAmount.amount > 0
      || cessionsSummary?.cessionsSummary.availableSurplus.amount <= 0
      || selectedCompany?.pendingReintegrations?.count > 0
      || amortizationDebt,
    [cessionsSummary, selectedCompany, amortizationDebt],
  );

  const WithdrawTooltip = useMemo(() => {
    if (cessionsSummary?.cessionsSummary.availableSurplus.amount <= 0) {
      return 'No tienes excedentes disponibles para retirar';
    }
    const reasons = [
      [cessionsSummary?.cessionsSummary.debtAmount.amount > 0, 'facturas en mora'],
      [selectedCompany?.pendingReintegrations?.count > 0, 'reintegros pendientes'],
      [amortizationDebt, 'un plan de pago moroso'],
    ]
      .filter(([condition]) => condition)
      .map(([, reason]) => reason);
    if (reasons.length === 0) return '';
    return `Tienes excedentes, pero no puedes retirarlos porque tienes ${joinWithAnd(reasons)}`;
  }, [cessionsSummary, selectedCompany, amortizationDebt]);

  const handleButton = useCallback(() => {
    setTrue();
  }, [setTrue]);

  return (
    <Box height="inherit" sx={{ position: 'relative' }}>
      <Button
        color="primary"
        variant="contained"
        fullWidth
        disableElevation
        onClick={handleButton}
        disabled={
              cessionsSummary?.cessionsSummary.availableSurplus.amount <= 0 || cantWithdrawSurplus
            }
        sx={{ lineHeight: '1.2', fontSize: { xs: '12px', md: '16px' }, height: '100%', borderRadius: 2 }}
        id="withdraw-surplus"
      >
        <Typography variant="small_3" fontWeight={700}>
          Retirar excedentes
        </Typography>
      </Button>
      {(cessionsSummary?.cessionsSummary.availableSurplus.amount <= 0 || cantWithdrawSurplus) && (
        <Tooltip title={WithdrawTooltip} classes={{ tooltip: ONBOARDING_TOOLTIP_CLASS_NAME }}>
          <InfoIcon
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              fontSize: { xs: '14px', md: '16px' },
              color: 'white',
              cursor: 'pointer',
              zIndex: 1,
            }}
          />
        </Tooltip>
      )}
      {open && <SurplusDialog open={open} onClose={setFalse} />}
    </Box>
  );
};

export default Surplus;
